/**
 * text-template.ts (P4-S2) the per-tenant Kommunikationsbausteine wire types + the in-code P-04
 * defaults (Stefan's Kap. 17.4 Kerntexte + 17.3 Handlungsbaustein).
 *
 * One text per (process_key, step_key, kanal, lang, anrede, segment), keyed by the Kap. 17.1
 * reference <Prozess>.<Schritt>-<Kanal>-<Sprache>-<Anrede>-<Segment>, used for E-Mail AND Brief.
 * Template bodies are tenant CONFIGURATION with {{placeholders}}, never customer data. Unknown
 * placeholders are left verbatim (visible, never silently dropped).
 *
 * NO PII here: the defaults carry generic copy only; the customer's name arrives at render time as
 * vars.anrede_zeile / vars.kunde (a letter TO the customer), never logged.
 *
 * [Source: packages/db/migrations/0173_p4_mahnwesen_params_text_template.sql] (the text_template table)
 */

export const TEXT_TEMPLATE_KANAL = ['EMAIL', 'PORTAL', 'BRIEF'] as const;
export type TextTemplateKanal = (typeof TEXT_TEMPLATE_KANAL)[number];

export const TEXT_TEMPLATE_SEGMENT = ['KMU', 'PRIV', 'VERS'] as const;
export type TextTemplateSegment = (typeof TEXT_TEMPLATE_SEGMENT)[number];

export const TEXT_TEMPLATE_LANG = ['de', 'en'] as const;
export type TextTemplateLang = (typeof TEXT_TEMPLATE_LANG)[number];

export const TEXT_TEMPLATE_ANREDE = ['sie', 'du'] as const;
export type TextTemplateAnrede = (typeof TEXT_TEMPLATE_ANREDE)[number];

/** The identity of a Kommunikationsbaustein (Kap. 17.1). */
export interface TextTemplateKey {
  process_key: string;
  step_key: string;
  kanal: TextTemplateKanal;
  lang: TextTemplateLang;
  anrede: TextTemplateAnrede;
  segment: TextTemplateSegment;
}

/** One resolved template, as listed in the admin surface (GET /admin/text-templates). */
export interface TextTemplateDto extends TextTemplateKey {
  subject: string;
  body: string;
  source: 'default' | 'tenant';
  is_active: boolean;
  updated_at: string | null;
}

/** PUT /admin/text-templates body. */
export type UpsertTextTemplateBody = TextTemplateKey & {
  subject: string;
  body: string;
  is_active?: boolean;
};

/**
 * Build the Kap. 17.1 reference, e.g. 'P-04.S9-EMAIL-DE-Sie-KMU'. Kanal upper, Sprache upper,
 * Anrede capitalised (Sie/Du), Segment as-is.
 */
export function buildTextTemplateRef(k: TextTemplateKey): string {
  const anrede = k.anrede === 'du' ? 'Du' : 'Sie';
  return `${k.process_key}.${k.step_key}-${k.kanal}-${k.lang.toUpperCase()}-${anrede}-${k.segment}`;
}

/**
 * Whitelisted placeholders. Only these `{{...}}` are substituted; any other `{{...}}` is left
 * verbatim (visible, never silently dropped). Rule #6: the values are passed in at render time and
 * never logged.
 */
export const TEXT_TEMPLATE_PLACEHOLDERS = [
  'anrede_zeile',
  'kunde',
  'kundennummer',
  'broker',
  'portal_link',
  // The validity of the portal link as dd.mm.yyyy (filled with {{portal_link}} while the holder is invited).
  'portal_gueltig_bis',
  'vorgangs_id',
  'zustelldatum',
  // P4-S4: the Nachforderung finding list (S20N). Built from stable codes, never free text.
  'befundliste',
  // The insurer letters (P-04.S22, P-05.S23): the customer with address/UID, the Policenliste, the
  // attached file, and for the Brokermandat the Brokercode VU, the requested Wirkungsdatum and the
  // hint paragraph when the insurer has no Brokercode for the broker yet.
  'kunde_adresse_uid',
  'policenliste',
  'anhang',
  'brokercode',
  'wirkungsdatum',
  'brokercode_hinweis',
  // The insurer reminders (P-04.S24, P-05.S26): the day the Meldung went out (dd.mm.yyyy, Zurich) and
  // the contracts still without a confirmation (the Policenliste of the Meldung).
  'datum_meldung',
  'offene_policen',
  // P6-S3 the Dankesbrief (P-06.S9/S9A): the Eingangsdatum, the Enddatum and the end of the portal's
  // read-only time (dd.mm.yyyy, Zurich), and the three sentence slots of the variants (17.1): the
  // opening sentence (Kündigung erhalten / wie besprochen), the care-until sentence (empty for
  // «sofort») and the portal sentence (empty without an active portal access).
  'eingangsdatum',
  // Also the last day of the mandate in the Auflösungsmeldung (P-06.S13, S13A, S16).
  'enddatum',
  'ende_portal_nachlauf',
  'eingang_satz',
  'betreuung_satz',
  'portal_satz',
  // The insurer sentence of the Dankesbrief, empty for a termination without a mandate (review C2).
  'versicherer_satz',
  // The Rücknahmemeldung (P-06.S19W): the request to register the broker again, filled only for an insurer
  // that already confirmed the Austragung; empty otherwise.
  'wiedereintragung_satz',
] as const;
export type TextTemplatePlaceholder = (typeof TEXT_TEMPLATE_PLACEHOLDERS)[number];

/** The P-04 steps: S9/S10/S11 = S2 reminders; S20 (Eingangsbestaetigung) + S20N (Nachforderung) = P4-S4. */
export const P04_STEP_KEYS = ['S9', 'S10', 'S11', 'S20', 'S20N'] as const;
export type P04StepKey = (typeof P04_STEP_KEYS)[number];
/** Only the reminder steps are index-addressed by the Mahnwesen stage engine (P4-S2). */
export const P04_REMINDER_STEP_KEYS = ['S9', 'S10', 'S11'] as const;
export type P04ReminderStepKey = (typeof P04_REMINDER_STEP_KEYS)[number];

/**
 * Substitute the whitelisted `{{placeholder}}` tokens in a string. An unknown token (not in the
 * whitelist, or without a provided value) is left verbatim. Pure; no IO/clock.
 */
export function applyTextTemplateVars(
  text: string,
  vars: Partial<Record<TextTemplatePlaceholder, string>>,
): string {
  let out = text;
  for (const name of TEXT_TEMPLATE_PLACEHOLDERS) {
    const value = vars[name];
    if (value == null) continue;
    out = out.split(`{{${name}}}`).join(value);
  }
  return out;
}

// The Handlungsbaustein P-04.S5 (Kap. 17.3): the Kundennummer line, Sie/Du, with the KMU+Du form
// using euch/eure. The placeholder stays as {{kundennummer}}.
// shortcut: no portal line, because the reminder engine has no portal link to fill (S1 also drops the
// line without one); add it back with {{portal_link}} once the engine passes a real link.
function handlungsbaustein(anrede: TextTemplateAnrede, segment: TextTemplateSegment): string {
  if (anrede === 'sie') {
    return (
      'Für die Zuordnung Ihrer Unterlagen und bei Rückfragen geben Sie bitte Ihre Kundennummer an. ' +
      'Ihre Kundennummer: {{kundennummer}}.'
    );
  }
  if (segment === 'KMU') {
    return (
      'Für die Zuordnung eurer Unterlagen und bei Rückfragen gebt bitte eure Kundennummer an. ' +
      'Eure Kundennummer: {{kundennummer}}.'
    );
  }
  return (
    'Für die Zuordnung deiner Unterlagen und bei Rückfragen gib bitte deine Kundennummer an. ' +
    'Deine Kundennummer: {{kundennummer}}.'
  );
}

function sieDu<T>(anrede: TextTemplateAnrede, sie: T, du: T): T {
  return anrede === 'sie' ? sie : du;
}

/** Stefan's 17.4 Kerntext per step, Sie/Du. KMU-Du uses euch; PRIV-Du uses dich. */
function kerntext(step: P04StepKey, anrede: TextTemplateAnrede, segment: TextTemplateSegment): string {
  const ihr = anrede === 'du' && segment === 'KMU';
  if (step === 'S9') {
    return anrede === 'sie'
      ? 'Vor einigen Tagen haben wir Ihnen das Analysemandat zur Unterzeichnung zugestellt. ' +
          'Falls Sie es bereits unterzeichnet haben, danken wir Ihnen herzlich.'
      : ihr
        ? 'Vor einigen Tagen haben wir euch das Analysemandat zur Unterzeichnung zugestellt. ' +
            'Falls ihr es bereits unterzeichnet habt, danken wir euch herzlich.'
        : 'Vor einigen Tagen haben wir dir das Analysemandat zur Unterzeichnung zugestellt. ' +
            'Falls du es bereits unterzeichnet hast, danken wir dir herzlich.';
  }
  if (step === 'S10') {
    return anrede === 'sie'
      ? 'Gerne möchten wir mit der Analyse beginnen. Dazu fehlt uns noch das unterzeichnete Analysemandat. ' +
          '{{broker}} ist bei Fragen gerne für Sie da.'
      : ihr
        ? 'Gerne möchten wir mit der Analyse beginnen. Dazu fehlt uns noch das unterzeichnete Analysemandat. ' +
            '{{broker}} ist bei Fragen gerne für euch da.'
        : 'Gerne möchten wir mit der Analyse beginnen. Dazu fehlt uns noch das unterzeichnete Analysemandat. ' +
            '{{broker}} ist bei Fragen gerne für dich da.';
  }
  if (step === 'S20') {
    // P4-S4: Eingangsbestaetigung (Status definitiv, Kap. 7.3 Schritt 20).
    return anrede === 'sie'
      ? 'Wir haben Ihr unterzeichnetes Analysemandat erhalten und die Kontrolle abgeschlossen. ' +
          'Das Mandat ist nun aktiv. Herzlichen Dank fuer Ihr Vertrauen.'
      : ihr
        ? 'Wir haben euer unterzeichnetes Analysemandat erhalten und die Kontrolle abgeschlossen. ' +
            'Das Mandat ist nun aktiv. Herzlichen Dank fuer euer Vertrauen.'
        : 'Wir haben dein unterzeichnetes Analysemandat erhalten und die Kontrolle abgeschlossen. ' +
            'Das Mandat ist nun aktiv. Herzlichen Dank fuer dein Vertrauen.';
  }
  if (step === 'S20N') {
    // P4-S4: Nachforderung (offene Punkte aus der Kontrolle). {{befundliste}} = Codes, nie Freitext.
    return anrede === 'sie'
      ? 'Bei der Kontrolle Ihres Analysemandats sind noch offene Punkte aufgefallen. ' +
          'Wir bitten Sie, die folgenden Punkte zu ergaenzen:\n{{befundliste}}'
      : ihr
        ? 'Bei der Kontrolle eures Analysemandats sind noch offene Punkte aufgefallen. ' +
            'Wir bitten euch, die folgenden Punkte zu ergaenzen:\n{{befundliste}}'
        : 'Bei der Kontrolle deines Analysemandats sind noch offene Punkte aufgefallen. ' +
            'Wir bitten dich, die folgenden Punkte zu ergaenzen:\n{{befundliste}}';
  }
  // S11
  return anrede === 'sie'
    ? 'Wir haben bisher kein unterzeichnetes Analysemandat erhalten. ' +
        '{{broker}} wird sich in den nächsten Tagen persönlich bei Ihnen melden.'
    : ihr
      ? 'Wir haben bisher kein unterzeichnetes Analysemandat erhalten. ' +
          '{{broker}} wird sich in den nächsten Tagen persönlich bei euch melden.'
      : 'Wir haben bisher kein unterzeichnetes Analysemandat erhalten. ' +
          '{{broker}} wird sich in den nächsten Tagen persönlich bei dir melden.';
}

const P04_SUBJECTS: Record<P04StepKey, string> = {
  S9: 'Erinnerung: Analysemandat zur Unterzeichnung',
  S10: 'Mahnung: Analysemandat noch nicht unterzeichnet',
  S11: 'Letzte Erinnerung: Analysemandat noch ausstehend',
  S20: 'Eingangsbestaetigung: Analysemandat erhalten',
  S20N: 'Nachforderung: Unterlagen zum Analysemandat',
};

/**
 * The generic Kerntext per step, Sie/Du, for a Vorgang without a mandate (a signed document from the
 * document generation): the same structure as the P-04 text, naming no mandate type.
 */
function genericKerntext(step: P04ReminderStepKey, anrede: TextTemplateAnrede, segment: TextTemplateSegment): string {
  const ihr = anrede === 'du' && segment === 'KMU';
  if (step === 'S9') {
    return anrede === 'sie'
      ? 'Am {{zustelldatum}} haben wir Ihnen ein Dokument zur Unterzeichnung zugestellt. ' +
          'Falls Sie es bereits unterzeichnet haben, danken wir Ihnen herzlich.'
      : ihr
        ? 'Am {{zustelldatum}} haben wir euch ein Dokument zur Unterzeichnung zugestellt. ' +
            'Falls ihr es bereits unterzeichnet habt, danken wir euch herzlich.'
        : 'Am {{zustelldatum}} haben wir dir ein Dokument zur Unterzeichnung zugestellt. ' +
            'Falls du es bereits unterzeichnet hast, danken wir dir herzlich.';
  }
  if (step === 'S10') {
    return anrede === 'sie'
      ? 'Uns fehlt noch das unterzeichnete Dokument, das wir Ihnen am {{zustelldatum}} zugestellt haben. ' +
          '{{broker}} ist bei Fragen gerne für Sie da.'
      : ihr
        ? 'Uns fehlt noch das unterzeichnete Dokument, das wir euch am {{zustelldatum}} zugestellt haben. ' +
            '{{broker}} ist bei Fragen gerne für euch da.'
        : 'Uns fehlt noch das unterzeichnete Dokument, das wir dir am {{zustelldatum}} zugestellt haben. ' +
            '{{broker}} ist bei Fragen gerne für dich da.';
  }
  // S11
  return anrede === 'sie'
    ? 'Wir haben das unterzeichnete Dokument bisher nicht erhalten. ' +
        '{{broker}} wird sich in den nächsten Tagen persönlich bei Ihnen melden.'
    : ihr
      ? 'Wir haben das unterzeichnete Dokument bisher nicht erhalten. ' +
          '{{broker}} wird sich in den nächsten Tagen persönlich bei euch melden.'
      : 'Wir haben das unterzeichnete Dokument bisher nicht erhalten. ' +
          '{{broker}} wird sich in den nächsten Tagen persönlich bei dir melden.';
}

const GENERIC_REMINDER_SUBJECTS: Record<P04ReminderStepKey, string> = {
  S9: 'Erinnerung: Dokument zur Unterzeichnung',
  S10: 'Mahnung: Dokument noch nicht unterzeichnet',
  S11: 'Letzte Erinnerung: Dokument noch ausstehend',
};

function buildDefaultBody(step: P04StepKey, anrede: TextTemplateAnrede, segment: TextTemplateSegment, core: string): string {
  const lines: string[] = ['{{anrede_zeile}}', '', core];
  // Handlungsbaustein P-04.S5 on S9 + S10 only (S11 has none, per 17.4).
  if (step === 'S9' || step === 'S10') {
    lines.push('', handlungsbaustein(anrede, segment));
  }
  lines.push('', sieDu(anrede, 'Freundliche Grüsse', 'Liebe Grüsse'), '{{broker}}');
  return lines.join('\n');
}

/**
 * The in-code P-04 defaults (used until a tenant saves its own text): every (step x anrede x segment) for the EMAIL kanal,
 * DE. Keyed by the EMAIL ref (buildTextTemplateRef). PORTAL/BRIEF and EN resolve through the EMAIL
 * ref (textgleiche Referenz, Kap. 17.2; EN = DE copy until Kap. 15 is closed). Built once at module load.
 */
export const P04_TEXT_DEFAULTS: Record<string, { subject: string; body: string }> = (() => {
  const out: Record<string, { subject: string; body: string }> = {};
  for (const step of P04_STEP_KEYS) {
    for (const anrede of TEXT_TEMPLATE_ANREDE) {
      for (const segment of ['KMU', 'PRIV'] as const) {
        const ref = buildTextTemplateRef({
          process_key: 'P-04',
          step_key: step,
          kanal: 'EMAIL',
          lang: 'de',
          anrede,
          segment,
        });
        out[ref] = { subject: P04_SUBJECTS[step], body: buildDefaultBody(step, anrede, segment, kerntext(step, anrede, segment)) };
      }
    }
  }
  return out;
})();

// ── P-04.S22: the Versicherer-Benachrichtigung of the Analysemandat (Stefan P4 17.4) ─────────────

const P04_S22_SUBJECT = 'Analysemandat {{kunde}} – Bitte um Bestätigung und Bestandesauskunft {{vorgangs_id}}';
const P04_S22_BODY = [
  'Sehr geehrte Damen und Herren',
  '',
  'Kunde: {{kunde_adresse_uid}}',
  '',
  'Die oben genannte Kundin bzw. der oben genannte Kunde hat uns ein Analysemandat erteilt. Das',
  'unterzeichnete Mandat finden Sie im Anhang ({{anhang}}).',
  '',
  'Wir bitten Sie um Bestätigung des Mandats sowie um eine Bestandesauskunft zu den folgenden',
  'Versicherungen:',
  '{{policenliste}}',
  '',
  'Bitte verwenden Sie bei Ihrer Antwort unsere Referenz {{vorgangs_id}}.',
  '',
  'Besten Dank für Ihre Unterstützung.',
  '',
  'Freundliche Grüsse',
  '{{broker}}',
].join('\n');

// P-04.S24 (Stefan P4 V2.0 17.4 verbatim): the reminder to an insurer that did not answer the S22 letter.
const P04_S24_SUBJECT = 'Erinnerung: Analysemandat {{kunde}} - Bestätigung {{vorgangs_id}}';
const P04_S24_BODY = [
  'Sehr geehrte Damen und Herren',
  '',
  'Am {{datum_meldung}} haben wir Ihnen das Analysemandat unseres Kunden {{kunde}} zugestellt (Referenz {{vorgangs_id}}). ' +
    'Wir bitten um Bestätigung und Zustellung der angefragten Unterlagen.',
  '',
  'Freundliche Grüsse',
  '{{broker}}',
].join('\n');

/** The in-code P-04 insurer defaults S22 and S24 (EMAIL, DE, segment VERS; insurer letters are Sie, the Du cell carries the same text). */
export const P04_INSURER_TEXT_DEFAULTS: Record<string, { subject: string; body: string }> = (() => {
  const out: Record<string, { subject: string; body: string }> = {};
  for (const anrede of TEXT_TEMPLATE_ANREDE) {
    const ref = buildTextTemplateRef({ process_key: 'P-04', step_key: 'S22', kanal: 'EMAIL', lang: 'de', anrede, segment: 'VERS' });
    out[ref] = { subject: P04_S22_SUBJECT, body: P04_S22_BODY };
    const s24 = buildTextTemplateRef({ process_key: 'P-04', step_key: 'S24', kanal: 'EMAIL', lang: 'de', anrede, segment: 'VERS' });
    out[s24] = { subject: P04_S24_SUBJECT, body: P04_S24_BODY };
  }
  return out;
})();

// ── P-05 Brokermandat (Stefan P5 V2.0 Kap. 17.3) ──────────────────────────────────────────────

/** The process keys a mandate text is stored under (text_template.process_key). */
export type MandateTextProcessKey = 'P-04' | 'P-05';

/**
 * The P-05 steps: S13/S14 = the customer reminders, S21 = the Eingangsbestätigung, S23 = the Meldung an
 * Versicherer, S26 = the Erinnerung Versicherer (this file's defaults).
 */
export const P05_STEP_KEYS = ['S13', 'S14', 'S21', 'S23', 'S26'] as const;
export type P05StepKey = (typeof P05_STEP_KEYS)[number];
/** The P-05 reminder step per stage: Erinnerung 1 = S13, Erinnerung 2 and 3 = S14 (17.3). */
export const P05_REMINDER_STEP_KEYS = ['S13', 'S14', 'S14'] as const;
export type P05ReminderStepKey = (typeof P05_REMINDER_STEP_KEYS)[number];
/** The P-05 steps with an in-code default in this file. */
export const P05_DEFAULT_STEP_KEYS = ['S13', 'S14', 'S21', 'S20N'] as const;
type P05DefaultStepKey = (typeof P05_DEFAULT_STEP_KEYS)[number];
/** The P-05 steps addressed to an insurer (segment VERS); the others go to the customer. */
export const P05_INSURER_STEP_KEYS: readonly P05StepKey[] = ['S23', 'S26'];

/** The text step of a reminder stage (1-based) of a mandate process. Pure. */
export function reminderStepKey(processKey: MandateTextProcessKey, stage: 1 | 2 | 3): P04ReminderStepKey | P05ReminderStepKey {
  const index = Math.min(Math.max(stage, 1), 3) - 1;
  return processKey === 'P-05' ? P05_REMINDER_STEP_KEYS[index]! : P04_REMINDER_STEP_KEYS[index]!;
}

/** The Eingangsbestätigung step of a mandate kind: P-04.S20 (Analysemandat), P-05.S21 (Brokermandat). */
export function eingangsbestaetigungStep(kind: 'analyse' | 'broker'): { processKey: MandateTextProcessKey; stepKey: 'S20' | 'S21' } {
  return kind === 'broker' ? { processKey: 'P-05', stepKey: 'S21' } : { processKey: 'P-04', stepKey: 'S20' };
}

/** The placeholders of the portal paragraph: filled only while the holder is invited. */
export const PORTAL_TEXT_PLACEHOLDERS: readonly TextTemplatePlaceholder[] = ['portal_link', 'portal_gueltig_bis'];

/**
 * Drop every paragraph (blank-line separated) that still holds one of the given placeholders after
 * the substitution: the portal paragraph of a P-05 text goes when there is no invitation link to put
 * in it, in a default text and in a tenant's own text alike. Pure.
 */
export function dropUnfilledParagraphs(text: string, placeholders: readonly TextTemplatePlaceholder[]): string {
  const tokens = placeholders.map((p) => `{{${p}}}`);
  return text
    .split(/\n{2,}/)
    .filter((paragraph) => !tokens.some((t) => paragraph.includes(t)))
    .join('\n\n');
}

// The Handlungsbaustein P-05.S8 (17.2) of a P-05 reminder, followed by the Kundennummer line
// (Sie, KMU-Du with euch/eure, PRIV-Du with dich/deine).
function p05Handlungsbaustein(anrede: TextTemplateAnrede, segment: TextTemplateSegment): string {
  const ihr = anrede === 'du' && segment === 'KMU';
  const sentence =
    anrede === 'sie'
      ? 'Damit wir Ihr Versicherungsportfolio übernehmen können, bitten wir Sie, das beiliegende Brokermandat zu unterzeichnen, ' +
          'am einfachsten direkt im Kundenportal, alternativ als Scan per Antwort auf diese E-Mail oder per Post. ' +
          'Bitte prüfen Sie die beiliegende Policenliste und ergänzen Sie fehlende Verträge.'
      : ihr
        ? 'Damit wir euer Versicherungsportfolio übernehmen können, bitten wir euch, das beiliegende Brokermandat zu unterzeichnen, ' +
            'am einfachsten direkt im Kundenportal, alternativ als Scan per Antwort auf diese E-Mail oder per Post. ' +
            'Bitte prüft die beiliegende Policenliste und ergänzt fehlende Verträge.'
        : 'Damit wir dein Versicherungsportfolio übernehmen können, bitten wir dich, das beiliegende Brokermandat zu unterzeichnen, ' +
            'am einfachsten direkt im Kundenportal, alternativ als Scan per Antwort auf diese E-Mail oder per Post. ' +
            'Bitte prüfe die beiliegende Policenliste und ergänze fehlende Verträge.';
  return `${sentence}\n\n${handlungsbaustein(anrede, segment)}`;
}

// The portal invitation paragraph (P-05.S8P, 17.2): only kept when {{portal_link}} is filled.
function p05PortalParagraph(anrede: TextTemplateAnrede, segment: TextTemplateSegment): string {
  const ihr = anrede === 'du' && segment === 'KMU';
  const intro =
    anrede === 'sie'
      ? 'Ihr persönliches Kundenportal: Hier sehen Sie Ihre Policen, Dokumente und Nachrichten jederzeit an einem Ort und können das Brokermandat digital unterzeichnen.'
      : ihr
        ? 'Euer persönliches Kundenportal: Hier seht ihr eure Policen, Dokumente und Nachrichten jederzeit an einem Ort und könnt das Brokermandat digital unterzeichnen.'
        : 'Dein persönliches Kundenportal: Hier siehst du deine Policen, Dokumente und Nachrichten jederzeit an einem Ort und kannst das Brokermandat digital unterzeichnen.';
  return `${intro}\nKundenportal jetzt aktivieren: {{portal_link}}\nPersönlicher Link, gültig bis {{portal_gueltig_bis}}, Anmeldung mit Zwei-Faktor.`;
}

/** Stefan's 17.3 Kerntext per P-05 step, Sie/Du. KMU-Du uses euch/eure; PRIV-Du uses dich/deine. */
function p05Kerntext(step: P05DefaultStepKey, anrede: TextTemplateAnrede, segment: TextTemplateSegment): string {
  const ihr = anrede === 'du' && segment === 'KMU';
  if (step === 'S13') {
    return anrede === 'sie'
      ? 'Vor einigen Tagen haben wir Ihnen das Brokermandat zur Unterzeichnung zugestellt. ' +
          'Falls Sie es bereits unterzeichnet haben, danken wir Ihnen herzlich.'
      : ihr
        ? 'Vor einigen Tagen haben wir euch das Brokermandat zur Unterzeichnung zugestellt. ' +
            'Falls ihr es bereits unterzeichnet habt, danken wir euch herzlich.'
        : 'Vor einigen Tagen haben wir dir das Brokermandat zur Unterzeichnung zugestellt. ' +
            'Falls du es bereits unterzeichnet hast, danken wir dir herzlich.';
  }
  if (step === 'S20N') {
    // The Nachforderung of a Brokermandat: the open points of the control, {{befundliste}} in words.
    return anrede === 'sie'
      ? 'Bei der Kontrolle Ihres unterzeichneten Brokermandats sind noch offene Punkte aufgefallen. ' +
          'Wir bitten Sie, die folgenden Punkte zu ergänzen:\n{{befundliste}}'
      : ihr
        ? 'Bei der Kontrolle eures unterzeichneten Brokermandats sind noch offene Punkte aufgefallen. ' +
            'Wir bitten euch, die folgenden Punkte zu ergänzen:\n{{befundliste}}'
        : 'Bei der Kontrolle deines unterzeichneten Brokermandats sind noch offene Punkte aufgefallen. ' +
            'Wir bitten dich, die folgenden Punkte zu ergänzen:\n{{befundliste}}';
  }
  if (step === 'S14') {
    return anrede === 'sie'
      ? 'Gerne übernehmen wir die Betreuung Ihrer Versicherungen. Dazu fehlt uns noch das unterzeichnete Brokermandat. ' +
          '{{broker}} ist bei Fragen gerne für Sie da.'
      : ihr
        ? 'Gerne übernehmen wir die Betreuung eurer Versicherungen. Dazu fehlt uns noch das unterzeichnete Brokermandat. ' +
            '{{broker}} ist bei Fragen gerne für euch da.'
        : 'Gerne übernehmen wir die Betreuung deiner Versicherungen. Dazu fehlt uns noch das unterzeichnete Brokermandat. ' +
            '{{broker}} ist bei Fragen gerne für dich da.';
  }
  // S21 Eingangsbestätigung
  return anrede === 'sie'
    ? 'Vielen Dank, wir haben Ihr unterzeichnetes Brokermandat erhalten. Wir informieren nun Ihre Versicherungsgesellschaften ' +
        'und lassen das Mandat eintragen. Über den Stand informieren wir Sie im Kundenportal.'
    : ihr
      ? 'Vielen Dank, wir haben euer unterzeichnetes Brokermandat erhalten. Wir informieren nun eure Versicherungsgesellschaften ' +
          'und lassen das Mandat eintragen. Über den Stand informieren wir euch im Kundenportal.'
      : 'Vielen Dank, wir haben dein unterzeichnetes Brokermandat erhalten. Wir informieren nun deine Versicherungsgesellschaften ' +
          'und lassen das Mandat eintragen. Über den Stand informieren wir dich im Kundenportal.';
}

const P05_SUBJECTS: Record<P05DefaultStepKey, string> = {
  S13: 'Erinnerung: Brokermandat zur Unterzeichnung',
  S14: 'Erinnerung: Brokermandat noch nicht unterzeichnet',
  S21: 'Eingangsbestätigung: Brokermandat erhalten',
  S20N: 'Nachforderung: Unterlagen zum Brokermandat',
};

function buildP05Body(step: P05DefaultStepKey, anrede: TextTemplateAnrede, segment: TextTemplateSegment): string {
  const lines: string[] = ['{{anrede_zeile}}', '', p05Kerntext(step, anrede, segment)];
  // 17.3: S13 = Kerntext + Handlungsbaustein + ggf. Portal-Einladung; S21 = Kerntext + ggf. Portal-Einladung.
  if (step === 'S13') lines.push('', p05Handlungsbaustein(anrede, segment));
  if (step === 'S13' || step === 'S21') lines.push('', p05PortalParagraph(anrede, segment));
  lines.push('', sieDu(anrede, 'Freundliche Grüsse', 'Liebe Grüsse'), '{{broker}}');
  return lines.join('\n');
}

/**
 * The in-code P-05 defaults (Stefan 17.3): every (S13, S14, S21) x anrede x (KMU, PRIV) for the EMAIL
 * and the PORTAL kanal (textgleich, 17.2), DE. BRIEF and EN resolve to the EMAIL/DE ref. Built once.
 */
export const P05_TEXT_DEFAULTS: Record<string, { subject: string; body: string }> = (() => {
  const out: Record<string, { subject: string; body: string }> = {};
  for (const step of P05_DEFAULT_STEP_KEYS) {
    for (const anrede of TEXT_TEMPLATE_ANREDE) {
      for (const segment of ['KMU', 'PRIV'] as const) {
        for (const kanal of ['EMAIL', 'PORTAL'] as const) {
          const ref = buildTextTemplateRef({ process_key: 'P-05', step_key: step, kanal, lang: 'de', anrede, segment });
          out[ref] = { subject: P05_SUBJECTS[step], body: buildP05Body(step, anrede, segment) };
        }
      }
    }
  }
  return out;
})();

// P-05.S23 (17.3 verbatim): the Meldung an Versicherer, also for the automatic transfer to a new
// insurer. The Brokercode hint paragraph is dropped by the sender when the insurer has the code.
const P05_S23_SUBJECT = 'Brokermandat {{kunde}} - Bitte um Eintragung {{vorgangs_id}}';
const P05_S23_BODY = [
  'Sehr geehrte Damen und Herren',
  '',
  'Unser Kunde {{kunde_adresse_uid}} hat uns mit beiliegendem, unterzeichnetem Brokermandat mit der Betreuung seiner ' +
    'Versicherungsverträge beauftragt. Wir bitten Sie, uns per {{wirkungsdatum}} als betreuenden Broker (Brokercode {{brokercode}}) ' +
    'für die folgenden Verträge einzutragen und uns die Eintragung unter Angabe der Referenz {{vorgangs_id}} zu bestätigen:',
  '{{policenliste}}',
  '',
  '{{brokercode_hinweis}}',
  '',
  'Bitte richten Sie die Korrespondenz zu diesen Verträgen künftig an uns. Das Mandat umfasst auch allfällige weitere ' +
    'Verträge unseres Kunden bei Ihrer Gesellschaft.',
  '',
  'Freundliche Grüsse',
  '{{broker}}',
].join('\n');

// P-05.S26 (17.3 verbatim): the one reminder to an insurer that did not confirm the registration.
const P05_S26_SUBJECT = 'Erinnerung: Brokermandat {{kunde}} - Eintragung {{vorgangs_id}}';
const P05_S26_BODY = [
  'Sehr geehrte Damen und Herren',
  '',
  'Am {{datum_meldung}} haben wir Ihnen das Brokermandat unseres Kunden {{kunde}} zur Eintragung zugestellt ' +
    '(Referenz {{vorgangs_id}}). Für folgende Verträge fehlt uns noch Ihre Bestätigung:',
  '{{offene_policen}}',
  '',
  'Besten Dank für Ihre Rückmeldung.',
  '',
  'Freundliche Grüsse',
  '{{broker}}',
].join('\n');

/** The in-code P-05 insurer defaults S23 and S26 (EMAIL, DE, segment VERS, Sie and Du alike). */
export const P05_INSURER_TEXT_DEFAULTS: Record<string, { subject: string; body: string }> = (() => {
  const out: Record<string, { subject: string; body: string }> = {};
  for (const anrede of TEXT_TEMPLATE_ANREDE) {
    const ref = buildTextTemplateRef({ process_key: 'P-05', step_key: 'S23', kanal: 'EMAIL', lang: 'de', anrede, segment: 'VERS' });
    out[ref] = { subject: P05_S23_SUBJECT, body: P05_S23_BODY };
    const s26 = buildTextTemplateRef({ process_key: 'P-05', step_key: 'S26', kanal: 'EMAIL', lang: 'de', anrede, segment: 'VERS' });
    out[s26] = { subject: P05_S26_SUBJECT, body: P05_S26_BODY };
  }
  return out;
})();

// P-06.S13: the Auflösungsmeldung to an insurer that registered the broker (Stefan P6 17.2).
const P06_S13_SUBJECT = 'Auflösung Brokermandat {{kunde}} per {{enddatum}} - {{vorgangs_id}}';
const P06_S13_BODY = [
  'Sehr geehrte Damen und Herren',
  '',
  'Das Brokermandat unseres Kunden {{kunde_adresse_uid}} endet per {{enddatum}}. Wir bitten Sie, uns per diesem Datum ' +
    'als betreuenden Broker (Brokercode {{brokercode}}) für die folgenden Verträge auszutragen und uns die Austragung unter ' +
    'Angabe der Referenz {{vorgangs_id}} zu bestätigen:',
  '{{policenliste}}',
  '',
  'Besten Dank für Ihre Bestätigung.',
  '',
  'Freundliche Grüsse',
  '{{broker}}',
].join('\n');

// P-06.S13A: the information to an insurer that only received the analysis (no confirmation awaited).
const P06_S13A_SUBJECT = 'Auflösung Analysemandat {{kunde}} per {{enddatum}} - {{vorgangs_id}}';
const P06_S13A_BODY = [
  'Sehr geehrte Damen und Herren',
  '',
  'Wir informieren Sie, dass das Mandat unseres Kunden {{kunde_adresse_uid}} per {{enddatum}} endet. ' +
    'Unsere Anfrage zu den folgenden Verträgen (Referenz {{vorgangs_id}}) ist damit hinfällig:',
  '{{policenliste}}',
  '',
  'Freundliche Grüsse',
  '{{broker}}',
].join('\n');

// P-06.S16: the one reminder to an insurer that did not confirm the Austragung.
const P06_S16_SUBJECT = 'Erinnerung: Auflösung Brokermandat {{kunde}} - {{vorgangs_id}}';
const P06_S16_BODY = [
  'Sehr geehrte Damen und Herren',
  '',
  'Am {{datum_meldung}} haben wir Ihnen die Auflösung des Brokermandats unseres Kunden {{kunde}} per {{enddatum}} ' +
    'gemeldet (Referenz {{vorgangs_id}}). Für folgende Verträge fehlt uns noch Ihre Bestätigung der Austragung:',
  '{{offene_policen}}',
  '',
  'Besten Dank für Ihre Rückmeldung.',
  '',
  'Freundliche Grüsse',
  '{{broker}}',
].join('\n');

// P-06.S19W: the Rücknahmemeldung after a Widerruf, as a reply to the Auflösungsmeldung (Stefan P6 17.2).
const P06_S19W_SUBJECT = 'Rücknahme Auflösung Brokermandat {{kunde}} - {{vorgangs_id}}';
const P06_S19W_BODY = [
  'Sehr geehrte Damen und Herren',
  '',
  'Mit unserer Meldung vom {{datum_meldung}} haben wir Ihnen die Auflösung des Mandats unseres Kunden {{kunde}} per ' +
    '{{enddatum}} mitgeteilt (Referenz {{vorgangs_id}}). Der Kunde hat die Kündigung widerrufen; das Mandat bleibt ' +
    'bestehen. Bitte betrachten Sie unsere Meldung als gegenstandslos.',
  '{{wiedereintragung_satz}}',
  '',
  'Freundliche Grüsse',
  '{{broker}}',
].join('\n');

/** The sentence of P-06.S19W for an insurer that already de-registered the broker (a confirmed Austragung). */
export const P06_S19W_WIEDEREINTRAGUNG_SATZ =
  'Bitte tragen Sie uns per sofort wieder als Broker ein und bestätigen Sie uns die Eintragung.';

/** The sentence of the P-05.S23 hint paragraph when the insurer has no Brokercode for the broker. */
export const P05_S23_BROKERCODE_HINT =
  'Unser Brokercode liegt Ihnen noch nicht vor. Bitte teilen Sie ihn uns zusammen mit Ihrer Bestätigung mit.';
/** What the P-05.S23 Brokercode parenthesis reads without a code. */
export const P05_S23_BROKERCODE_MISSING = 'folgt';

// ── P-06 (P6 Mandatsauflösung) ────────────────────────────────────────────────────────────────

/** The P-06 customer steps: S9 the Dankesbrief (Brokermandat), S9A (Analysemandat), S23 (S7, portal). */
export const P06_STEP_KEYS = ['S9', 'S9A', 'S13', 'S13A', 'S16', 'S19W', 'S23'] as const;
export type P06StepKey = (typeof P06_STEP_KEYS)[number];
/** The P-06 insurer steps (VERS): S13, S13A, S16 (S4) and S19W (S5). */
export const P06_INSURER_STEP_KEYS: readonly P06StepKey[] = ['S13', 'S13A', 'S16', 'S19W'];

// The Dankesbrief (17.1 Kerntexte). The variant sentences are slots the sender fills (manual vs
// customer Kündigung, «sofort», portal access); an empty slot leaves an empty paragraph the sender
// drops, so a tenant text never reads half a sentence.
function p06S9Body(step: 'S9' | 'S9A', anrede: TextTemplateAnrede, segment: TextTemplateSegment): string {
  const ihr = anrede === 'du' && segment === 'KMU';
  const closing =
    step === 'S9'
      ? anrede === 'sie'
        ? 'Herzlichen Dank für die angenehme Zusammenarbeit und das Vertrauen, das Sie uns entgegengebracht haben. ' +
          (segment === 'KMU' ? 'Wir wünschen Ihnen und Ihrem Unternehmen alles Gute.' : 'Wir wünschen Ihnen alles Gute.')
        : ihr
          ? 'Herzlichen Dank für die schöne Zusammenarbeit und euer Vertrauen. Wir wünschen euch alles Gute.'
          : 'Herzlichen Dank für die schöne Zusammenarbeit und dein Vertrauen. Wir wünschen dir alles Gute.'
      : anrede === 'sie'
        ? 'Herzlichen Dank, dass Sie uns die Analyse Ihrer Versicherungssituation anvertraut haben. Gerne sind wir auch künftig für Sie da.'
        : ihr
          ? 'Herzlichen Dank, dass ihr uns die Analyse eurer Versicherungssituation anvertraut habt. Gerne sind wir auch künftig für euch da.'
          : 'Herzlichen Dank, dass du uns die Analyse deiner Versicherungssituation anvertraut hast. Gerne sind wir auch künftig für dich da.';
  const greeting = anrede === 'sie' ? 'Freundliche Grüsse' : 'Liebe Grüsse';
  return [
    '{{anrede_zeile}}',
    '',
    '{{eingang_satz}} {{betreuung_satz}}',
    ...(step === 'S9' ? ['', '{{versicherer_satz}}'] : []),
    '',
    '{{portal_satz}}',
    '',
    closing,
    '',
    greeting,
    '{{broker}}',
  ].join('\n');
}

/** The P-06 customer defaults: S9 and S9A x {sie, du} x {KMU, PRIV}, EMAIL/DE (BRIEF and PORTAL fall to it). */
export const P06_TEXT_DEFAULTS: Record<string, { subject: string; body: string }> = (() => {
  const out: Record<string, { subject: string; body: string }> = {};
  for (const step of ['S9', 'S9A'] as const) {
    for (const anrede of TEXT_TEMPLATE_ANREDE) {
      for (const segment of ['KMU', 'PRIV'] as const) {
        const ref = buildTextTemplateRef({ process_key: 'P-06', step_key: step, kanal: 'EMAIL', lang: 'de', anrede, segment });
        out[ref] = {
          subject: step === 'S9' ? 'Auflösung des Brokermandats per {{enddatum}}' : 'Abschluss des Analysemandats per {{enddatum}}',
          body: p06S9Body(step, anrede, segment),
        };
      }
    }
  }
  return out;
})();

/** The in-code P-06 insurer defaults S13, S13A, S16 and S19W (EMAIL, DE, segment VERS, Sie and Du alike). */
export const P06_INSURER_TEXT_DEFAULTS: Record<string, { subject: string; body: string }> = (() => {
  const out: Record<string, { subject: string; body: string }> = {};
  const texts: Record<'S13' | 'S13A' | 'S16' | 'S19W', { subject: string; body: string }> = {
    S13: { subject: P06_S13_SUBJECT, body: P06_S13_BODY },
    S13A: { subject: P06_S13A_SUBJECT, body: P06_S13A_BODY },
    S16: { subject: P06_S16_SUBJECT, body: P06_S16_BODY },
    S19W: { subject: P06_S19W_SUBJECT, body: P06_S19W_BODY },
  };
  for (const anrede of TEXT_TEMPLATE_ANREDE) {
    for (const step of ['S13', 'S13A', 'S16', 'S19W'] as const) {
      out[buildTextTemplateRef({ process_key: 'P-06', step_key: step, kanal: 'EMAIL', lang: 'de', anrede, segment: 'VERS' })] = texts[step];
    }
  }
  return out;
})();

/**
 * Look up the in-code default for a key: the process's own defaults only (a P-05 key never gets
 * P-04 wording). P-04 resolves PORTAL/BRIEF and EN to the EMAIL/DE ref; P-05 has PORTAL cells and
 * resolves BRIEF and EN to EMAIL/DE. Null when the process has no default for the step.
 */
export function resolveTextTemplateDefault(key: TextTemplateKey): { subject: string; body: string; ref: string } | null {
  if (key.process_key === 'P-05') {
    const kanal = key.kanal === 'PORTAL' && key.segment !== 'VERS' ? 'PORTAL' : 'EMAIL';
    const ref = buildTextTemplateRef({ ...key, kanal, lang: 'de' });
    const hit = P05_TEXT_DEFAULTS[ref] ?? P05_INSURER_TEXT_DEFAULTS[ref];
    return hit ? { ...hit, ref } : null;
  }
  if (key.process_key === 'P-06') {
    // One text for mail, letter and portal (Stefan 17.1): BRIEF, PORTAL and EN fall to EMAIL/DE. Never P-04.
    const ref = buildTextTemplateRef({ ...key, kanal: 'EMAIL', lang: 'de' });
    const hit = P06_TEXT_DEFAULTS[ref] ?? P06_INSURER_TEXT_DEFAULTS[ref];
    return hit ? { ...hit, ref } : null;
  }
  const emailRef = buildTextTemplateRef({ ...key, kanal: 'EMAIL', lang: 'de' });
  const hit = P04_TEXT_DEFAULTS[emailRef] ?? P04_INSURER_TEXT_DEFAULTS[emailRef];
  return hit ? { ...hit, ref: emailRef } : null;
}

/**
 * The admin list matrix of a process: the steps a tenant can edit, each with its segments. P-04:
 * the reminders S9-S11 (KMU, PRIV); its insurer letter S22 resolves through the registry too but is
 * not on the Mahnwesen page. P-05: S13, S14, S21 (KMU, PRIV) and the insurer steps S23, S26
 * (VERS). Unknown processes list nothing.
 */
export function textTemplateListMatrix(processKey: string): Array<{ step: string; segments: TextTemplateSegment[] }> {
  if (processKey === 'P-04') return P04_REMINDER_STEP_KEYS.map((step) => ({ step, segments: ['KMU', 'PRIV'] }));
  if (processKey === 'P-05') {
    return P05_STEP_KEYS.map((step) => ({
      step,
      segments: P05_INSURER_STEP_KEYS.includes(step) ? ['VERS'] : ['KMU', 'PRIV'],
    }));
  }
  if (processKey === 'P-06') {
    return P06_STEP_KEYS.map((step) => ({
      step,
      segments: P06_INSURER_STEP_KEYS.includes(step) ? ['VERS'] : ['KMU', 'PRIV'],
    }));
  }
  return [];
}

/**
 * The pseudo process key of the generic reminder texts. In-code only: text_template's CHECK allows
 * P-nn keys, so a tenant cannot store its own generic texts yet.
 */
export const GENERIC_REMINDER_PROCESS_KEY = 'GENERIC' as const;

/**
 * The in-code generic reminder defaults, for a Vorgang without a mandate (a signed document from the
 * document generation; review decision 2026-10-20): every (reminder step x anrede x segment) for the
 * EMAIL kanal, DE, keyed by the EMAIL ref with process key GENERIC. Same shape as P04_TEXT_DEFAULTS.
 */
export const GENERIC_REMINDER_TEXT_DEFAULTS: Record<string, { subject: string; body: string }> = (() => {
  const out: Record<string, { subject: string; body: string }> = {};
  for (const step of P04_REMINDER_STEP_KEYS) {
    for (const anrede of TEXT_TEMPLATE_ANREDE) {
      for (const segment of ['KMU', 'PRIV'] as const) {
        const ref = buildTextTemplateRef({
          process_key: GENERIC_REMINDER_PROCESS_KEY,
          step_key: step,
          kanal: 'EMAIL',
          lang: 'de',
          anrede,
          segment,
        });
        out[ref] = {
          subject: GENERIC_REMINDER_SUBJECTS[step],
          body: buildDefaultBody(step, anrede, segment, genericKerntext(step, anrede, segment)),
        };
      }
    }
  }
  return out;
})();

/**
 * Look up the generic reminder default for a key, whatever its process key (PORTAL/BRIEF and EN
 * resolve to the EMAIL/DE ref; a segment without a text, VERS, uses KMU).
 */
export function resolveGenericReminderTextDefault(key: TextTemplateKey): { subject: string; body: string; ref: string } {
  const segment = key.segment === 'PRIV' ? 'PRIV' : 'KMU';
  // Only the reminder steps have a generic text (the S4 steps S20/S20N always name the Analysemandat).
  const step: P04ReminderStepKey = (P04_REMINDER_STEP_KEYS as readonly string[]).includes(key.step_key)
    ? (key.step_key as P04ReminderStepKey)
    : 'S11';
  const ref = buildTextTemplateRef({ ...key, process_key: GENERIC_REMINDER_PROCESS_KEY, step_key: step, segment, kanal: 'EMAIL', lang: 'de' });
  return { ...GENERIC_REMINDER_TEXT_DEFAULTS[ref]!, ref };
}
